/**
 * PHSIMS Developer Portal — App Shell
 *
 * Handles authentication (email/password via JWT), invite acceptance
 * with password setup, forgot password, and renders the main dashboard.
 *
 * UI mirrors the WebAdmin login page: ZNPHI logo, orange ribbon,
 * green gradient button, AppFooter with powered-by-iswe branding.
 *
 * Security model:
 * - JWT stored in localStorage (developer's browser only)
 * - All API calls use Authorization: Bearer <jwt> to /v1/* endpoints
 * - API keys shown partially masked in the dashboard
 * - Invite tokens are one-time use, expire in 7 days
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Login as LoginIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  ContentCopy as CopyIcon,
  Dashboard as DashboardIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  PersonAdd as RegisterIcon,
  PersonOutline as PersonIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { TEXT } from './theme/colors';

// Lazy-load the full dashboard to keep initial bundle small
const DeveloperPortal = React.lazy(() => import('./pages/DeveloperPortal'));

// ── Storage Keys ────────────────────────────────────────────────────────────

const STORAGE_TOKEN = 'phsims_dev_token';
const STORAGE_REMEMBER = 'phsims_dev_remember';

// ── Brand constants (mirror WebAdmin LoginPage) ─────────────────────────────

const BRAND = {
  green: '#198a4b',
  greenDark: '#0d5c2e',
  orange: '#ef7d00',
  gray: '#475569',
  grayLight: '#64748b',
  bg: '#0d3322',
};

// ── Shared styles (mirror WebAdmin LoginPage) ───────────────────────────────

const fullPageSx = {
  height: '100vh',
  width: '100vw',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  background: BRAND.bg,
  position: 'fixed' as const,
  top: 0,
  left: 0,
  p: { xs: 2, sm: 0 },
};

const cardSx = {
  background: 'white',
  padding: { xs: '24px 20px', sm: '32px 40px' },
  borderRadius: { xs: '16px', sm: '20px' },
  boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
  width: { xs: '100%', sm: 460 },
  maxWidth: { xs: '100%', sm: '94%' },
  position: 'relative' as const,
  zIndex: 1,
  borderTop: `5px solid ${BRAND.orange}`,
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    fontWeight: 500,
    minHeight: '48px',
    '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: BRAND.green },
    '&.Mui-focused fieldset': { borderColor: BRAND.green },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 12px 12px 0',
    fontSize: '14px',
  },
};

const submitBtnSx = {
  mt: 0.5,
  py: 1.2,
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none' as const,
  background: `linear-gradient(135deg, ${BRAND.green} 0%, #14703d 100%)`,
  boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
  transition: 'all 0.3s',
  '&:hover': {
    background: `linear-gradient(135deg, ${BRAND.orange} 0%, #f59e0b 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
  },
  '&:active': { transform: 'translateY(0)' },
  '&:disabled': {
    background: '#cbd5e1',
    boxShadow: 'none',
    transform: 'none',
  },
};

// ── API helpers ─────────────────────────────────────────────────────────────

interface ConsumerInfo {
  id?: number;
  name: string;
  organization: string;
  email: string;
  tier: string;
}

interface InviteInfo {
  consumer: ConsumerInfo;
  message: string;
}

interface LoginResult {
  token: string;
  consumer: ConsumerInfo;
}

interface RegisterResult {
  token: string;
  api_key: string;
  key_prefix: string;
  key_suffix: string;
  consumer: ConsumerInfo;
}

interface AuthMeResult {
  consumer: ConsumerInfo;
  keys: { id: number; name: string; masked_key: string; prefix: string }[];
}

async function resolveInvite(token: string): Promise<InviteInfo> {
  const res = await fetch(`/v1/invite/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.error?.message || body.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

async function loginApi(email: string, password: string, remember: boolean): Promise<LoginResult> {
  const res = await fetch('/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, remember }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.error?.message || body.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

async function registerApi(inviteToken: string, password: string, remember: boolean): Promise<RegisterResult> {
  const res = await fetch('/v1/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invite_token: inviteToken, password, remember }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.error?.message || body.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

async function validateToken(jwt: string): Promise<AuthMeResult | null> {
  try {
    const res = await fetch('/v1/auth/me', {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function forgotPasswordApi(email: string): Promise<void> {
  await fetch('/v1/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  // Always succeed to prevent email enumeration
}

async function resetPasswordApi(token: string, password: string): Promise<{ message: string }> {
  const res = await fetch('/v1/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.error?.message || body.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── AppFooter (mirrors WebAdmin AppFooter auth variant) ─────────────────────

const AppFooter: React.FC = () => (
  <Box
    sx={{
      mt: 2.5,
      pt: 1.5,
      borderTop: '1px solid #f1f5f9',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Typography
      sx={{
        color: BRAND.gray,
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      &copy; {new Date().getFullYear()} Zambia National Public Health Institute
    </Typography>
    <Box
      component="a"
      href="https://iswesolutions.com"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        textDecoration: 'none',
        transition: 'all 0.2s ease',
        px: 1.5,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: 'rgba(25, 138, 75, 0.06)',
        border: '1px solid rgba(25, 138, 75, 0.12)',
        flexShrink: 0,
        minWidth: 'fit-content',
        '&:hover': {
          opacity: 0.8,
          bgcolor: 'rgba(25, 138, 75, 0.1)',
        },
      }}
    >
      <Box
        component="img"
        src="/powered-by-iswe.svg"
        alt="Powered by ISWE Analytics"
        sx={{
          height: 38,
          width: 'auto',
          maxWidth: '100%',
          objectFit: 'contain',
        }}
      />
    </Box>
  </Box>
);

// ── Portal Header (ZNPHI logo + system name, mirrors WebAdmin LoginPage) ────

const PortalHeader: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3 } }}>
    <Box
      component="img"
      src="/znphi_logo.jpg"
      alt="ZNPHI Logo"
      sx={{
        width: { xs: 70, sm: 90 },
        height: 'auto',
        borderRadius: '8px',
        mb: 1.5,
        objectFit: 'contain',
      }}
    />
    <Typography
      sx={{
        color: BRAND.greenDark,
        fontSize: { xs: '16px', sm: '20px' },
        fontWeight: 800,
        lineHeight: 1.3,
        mb: 0.5,
        letterSpacing: '0.2px',
      }}
    >
      Public Health Security &amp;<br />Information Management System
    </Typography>
    <Typography
      variant="h4"
      sx={{
        fontSize: { xs: '24px', sm: '28px' },
        fontWeight: 800,
        letterSpacing: '2px',
        mt: 0.5,
        color: BRAND.orange,
      }}
    >
      {subtitle || 'Developer Portal'}
    </Typography>
  </Box>
);

// ── View type ───────────────────────────────────────────────────────────────

type ViewType =
  | 'login'
  | 'forgot-password'
  | 'reset-password'
  | 'invite-loading'
  | 'invite-error'
  | 'register'
  | 'register-success'
  | 'dashboard';

// ── App Component ───────────────────────────────────────────────────────────

const App: React.FC = () => {
  // Auth state
  const [jwtToken, setJwtToken] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [autoLoginChecked, setAutoLoginChecked] = useState(false);

  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  // Invite state
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Registration state
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);
  const [regResult, setRegResult] = useState<RegisterResult | null>(null);

  // Forgot password state
  const [view, setView] = useState<ViewType>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Reset password state
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const tierColors: Record<string, string> = {
    free: TEXT.secondary,
    basic: '#1e4d8c',
    standard: BRAND.greenDark,
    premium: '#c87533',
    enterprise: '#3730a3',
  };

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // ── Auto-login from localStorage ────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    const reset = params.get('reset');

    if (reset) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      window.history.replaceState({}, '', url.pathname + url.search);
      setResetToken(reset);
      setView('reset-password');
      setAutoLoginChecked(true);
      return;
    }

    if (invite) {
      const url = new URL(window.location.href);
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url.pathname + url.search);

      setInviteToken(invite);
      setInviteLoading(true);
      setView('invite-loading');
      resolveInvite(invite)
        .then((info) => {
          setInviteInfo(info);
          if (info.consumer.email) setEmail(info.consumer.email);
          setView('register');
        })
        .catch((err) => {
          setInviteError(err.message || 'This invite link is invalid or has expired.');
          setView('invite-error');
        })
        .finally(() => {
          setInviteLoading(false);
          setAutoLoginChecked(true);
        });
      return;
    }

    const storedToken = localStorage.getItem(STORAGE_TOKEN);
    const shouldRemember = localStorage.getItem(STORAGE_REMEMBER) === 'true';

    if (storedToken && shouldRemember) {
      setLoginLoading(true);
      validateToken(storedToken)
        .then((result) => {
          if (result) {
            setJwtToken(storedToken);
            setAuthenticated(true);
            setView('dashboard');
          } else {
            localStorage.removeItem(STORAGE_TOKEN);
          }
        })
        .finally(() => {
          setLoginLoading(false);
          setAutoLoginChecked(true);
        });
    } else {
      setAutoLoginChecked(true);
    }
  }, []);

  // ── Login handler ───────────────────────────────────────────────

  const handleLogin = useCallback(async () => {
    if (!email.trim() || !password) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const result = await loginApi(email.trim(), password, remember);
      setJwtToken(result.token);
      if (remember) {
        localStorage.setItem(STORAGE_TOKEN, result.token);
        localStorage.setItem(STORAGE_REMEMBER, 'true');
      }
      setAuthenticated(true);
      setView('dashboard');
    } catch (err: any) {
      setLoginError(err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  }, [email, password, remember]);

  // ── Registration handler ────────────────────────────────────────

  const handleRegister = useCallback(async () => {
    if (!inviteToken) return;
    if (regPassword.length < 8) {
      setRegError('Password must be at least 8 characters');
      return;
    }
    if (regPassword !== regPasswordConfirm) {
      setRegError('Passwords do not match');
      return;
    }
    setRegLoading(true);
    setRegError(null);
    try {
      const result = await registerApi(inviteToken, regPassword, remember);
      setRegResult(result);
      setJwtToken(result.token);
      localStorage.setItem(STORAGE_TOKEN, result.token);
      localStorage.setItem(STORAGE_REMEMBER, 'true');
      setView('register-success');
    } catch (err: any) {
      setRegError(err.message || 'Registration failed');
    } finally {
      setRegLoading(false);
    }
  }, [inviteToken, regPassword, regPasswordConfirm, remember]);

  // ── Forgot password handler ─────────────────────────────────────

  const handleForgotPassword = useCallback(async () => {
    if (!forgotEmail.trim() || !isValidEmail(forgotEmail)) return;
    setForgotLoading(true);
    try {
      await forgotPasswordApi(forgotEmail.trim());
    } catch {
      // Always succeed to prevent enumeration
    } finally {
      setForgotLoading(false);
      setForgotSuccess(true);
    }
  }, [forgotEmail]);

  // ── Reset password handler ──────────────────────────────────────

  const handleResetPassword = useCallback(async () => {
    if (!resetToken) return;
    if (resetNewPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    setResetLoading(true);
    setResetError(null);
    try {
      await resetPasswordApi(resetToken, resetNewPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err.message || 'Password reset failed');
    } finally {
      setResetLoading(false);
    }
  }, [resetToken, resetNewPassword, resetConfirmPassword]);

  // ── Logout handler ──────────────────────────────────────────────

  const handleLogout = useCallback(() => {
    setAuthenticated(false);
    setJwtToken('');
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_REMEMBER);
    setRegResult(null);
    setInviteToken(null);
    setInviteInfo(null);
    setEmail('');
    setPassword('');
    setView('login');
  }, []);

  // ── Loading screen (auto-login check) ───────────────────────────

  if (!autoLoginChecked || (loginLoading && !email)) {
    return (
      <Box sx={{ ...fullPageSx }}>
        <CircularProgress sx={{ color: BRAND.orange }} />
      </Box>
    );
  }

  // ── Invite: loading ─────────────────────────────────────────────

  if (view === 'invite-loading') {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx, textAlign: 'center' }}>
          <PortalHeader />
          <CircularProgress size={40} sx={{ color: BRAND.green, my: 3 }} />
          <Typography color="text.secondary">Verifying your invite...</Typography>
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Invite: error ───────────────────────────────────────────────

  if (view === 'invite-error') {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx }}>
          <PortalHeader />
          <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
            {inviteError}
          </Alert>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'center' }}
          >
            This invite link may have expired or already been used. Contact your
            administrator for a new invite.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            startIcon={<BackIcon />}
            onClick={() => {
              setInviteError(null);
              setInviteToken(null);
              setView('login');
            }}
            sx={{ ...submitBtnSx }}
          >
            Go to Sign In
          </Button>
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Registration: API key shown once ────────────────────────────

  if (view === 'register-success' && regResult) {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx, width: { xs: '100%', sm: 520 } }}>
          <PortalHeader />

          <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>
            Welcome, <strong>{regResult.consumer.name}</strong>! Your account is
            ready.
          </Alert>

          <Alert
            severity="warning"
            icon={<KeyIcon />}
            sx={{ mb: 3, borderRadius: '10px' }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Save your API key now — it will not be shown again
            </Typography>
            <Typography variant="body2">
              Use this key in your app to authenticate with the PHSIMS API.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Your API Key"
            value={regResult.api_key}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 14 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy key">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigator.clipboard.writeText(regResult.api_key)
                      }
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, ...inputSx }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label={regResult.consumer.organization || 'No org'}
              size="small"
            />
            <Chip
              label={regResult.consumer.tier}
              size="small"
              sx={{
                bgcolor:
                  tierColors[regResult.consumer.tier] || TEXT.secondary,
                color: 'white',
              }}
            />
            <Chip
              label={`Key: ${regResult.key_prefix}...${regResult.key_suffix}`}
              size="small"
              variant="outlined"
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<DashboardIcon />}
            onClick={() => {
              setAuthenticated(true);
              setView('dashboard');
            }}
            sx={{ ...submitBtnSx }}
          >
            Continue to Dashboard
          </Button>
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Registration: set password form ─────────────────────────────

  if (view === 'register' && inviteInfo && !regResult) {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx }}>
          <PortalHeader subtitle="Create Account" />

          <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
            Welcome, <strong>{inviteInfo.consumer.name}</strong>
            {inviteInfo.consumer.organization &&
              ` (${inviteInfo.consumer.organization})`}
            ! Set a password to complete your registration.
          </Alert>

          {regError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              {regError}
            </Alert>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRegister();
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                placeholder="Email"
                value={inviteInfo.consumer.email || ''}
                disabled
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                placeholder="Password (min 8 characters)"
                type={showRegPassword ? 'text' : 'password'}
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                autoFocus
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        sx={{
                          color: '#94a3b8',
                          '&:hover': {
                            color: BRAND.orange,
                            bgcolor: 'rgba(239,125,0,0.08)',
                          },
                        }}
                      >
                        {showRegPassword ? (
                          <HideIcon fontSize="small" />
                        ) : (
                          <ShowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                placeholder="Confirm password"
                type={showRegPassword ? 'text' : 'password'}
                value={regPasswordConfirm}
                onChange={(e) => setRegPasswordConfirm(e.target.value)}
                error={
                  regPasswordConfirm.length > 0 &&
                  regPassword !== regPasswordConfirm
                }
                helperText={
                  regPasswordConfirm.length > 0 &&
                  regPassword !== regPasswordConfirm
                    ? 'Passwords do not match'
                    : ''
                }
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={!regPassword || !regPasswordConfirm || regLoading}
                startIcon={
                  regLoading ? (
                    <CircularProgress size={18} sx={{ color: 'white' }} />
                  ) : (
                    <RegisterIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={submitBtnSx}
              >
                {regLoading
                  ? 'Creating account...'
                  : 'Create Account & Get API Key'}
              </Button>
            </Box>
          </form>
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Forgot Password ─────────────────────────────────────────────

  if (view === 'forgot-password') {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx }}>
          <PortalHeader subtitle="Password Reset" />

          {forgotSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <SuccessIcon sx={{ fontSize: 56, color: '#198754', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: BRAND.greenDark,
                  fontSize: { xs: '16px', sm: '18px' },
                }}
              >
                Check Your Email
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: BRAND.grayLight,
                  mb: 2,
                  fontSize: { xs: '13px', sm: '14px' },
                  px: 1,
                }}
              >
                If an account exists with <strong>{forgotEmail}</strong>, you
                will receive a password reset link shortly.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  mb: 3,
                  fontSize: { xs: '11px', sm: '12px' },
                  px: 1,
                  fontStyle: 'italic',
                }}
              >
                Please also check your spam/junk folder. The link expires in 24
                hours.
              </Typography>
              <Button
                variant="contained"
                startIcon={<BackIcon />}
                onClick={() => {
                  setForgotSuccess(false);
                  setForgotEmail('');
                  setView('login');
                }}
                sx={{ ...submitBtnSx, px: 3 }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleForgotPassword();
              }}
            >
              <Typography
                sx={{
                  color: BRAND.grayLight,
                  fontSize: '13px',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                Enter your email address to receive a password reset link
              </Typography>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <TextField
                  placeholder="Email address"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  autoFocus
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={forgotLoading}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon
                          sx={{ color: BRAND.green, fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={
                    forgotLoading ||
                    !forgotEmail ||
                    !isValidEmail(forgotEmail)
                  }
                  startIcon={
                    forgotLoading ? undefined : (
                      <SendIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={submitBtnSx}
                >
                  {forgotLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <Typography
                    component="span"
                    onClick={() => {
                      setForgotEmail('');
                      setView('login');
                    }}
                    sx={{
                      color: BRAND.grayLight,
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: BRAND.orange,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back to login
                  </Typography>
                </Box>
              </Box>
            </form>
          )}
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Reset Password (from email link) ────────────────────────────

  if (view === 'reset-password') {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx }}>
          <PortalHeader subtitle="Reset Password" />

          {resetSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <SuccessIcon sx={{ fontSize: 56, color: '#198754', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: BRAND.greenDark,
                  fontSize: { xs: '16px', sm: '18px' },
                }}
              >
                Password Reset Complete
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: BRAND.grayLight,
                  mb: 3,
                  fontSize: { xs: '13px', sm: '14px' },
                }}
              >
                Your password has been updated successfully. You can now sign in
                with your new password.
              </Typography>
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => {
                  setResetToken(null);
                  setResetNewPassword('');
                  setResetConfirmPassword('');
                  setResetSuccess(false);
                  setView('login');
                }}
                sx={{ ...submitBtnSx, px: 3 }}
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword();
              }}
            >
              <Typography
                sx={{
                  color: BRAND.grayLight,
                  fontSize: '13px',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                Choose a new password for your account
              </Typography>

              {resetError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
                  {resetError}
                </Alert>
              )}

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <TextField
                  fullWidth
                  placeholder="New password (min 8 characters)"
                  type={showResetPassword ? 'text' : 'password'}
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  autoFocus
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{ color: BRAND.green, fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowResetPassword(!showResetPassword)
                          }
                          sx={{
                            color: '#94a3b8',
                            '&:hover': {
                              color: BRAND.orange,
                              bgcolor: 'rgba(239,125,0,0.08)',
                            },
                          }}
                        >
                          {showResetPassword ? (
                            <HideIcon fontSize="small" />
                          ) : (
                            <ShowIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <TextField
                  fullWidth
                  placeholder="Confirm new password"
                  type={showResetPassword ? 'text' : 'password'}
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  error={
                    resetConfirmPassword.length > 0 &&
                    resetNewPassword !== resetConfirmPassword
                  }
                  helperText={
                    resetConfirmPassword.length > 0 &&
                    resetNewPassword !== resetConfirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{ color: BRAND.green, fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={
                    resetLoading ||
                    !resetNewPassword ||
                    !resetConfirmPassword
                  }
                  startIcon={
                    resetLoading ? undefined : (
                      <LockIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={submitBtnSx}
                >
                  {resetLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <Typography
                    component="span"
                    onClick={() => {
                      setResetToken(null);
                      setResetNewPassword('');
                      setResetConfirmPassword('');
                      setResetError(null);
                      setView('login');
                    }}
                    sx={{
                      color: BRAND.grayLight,
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: BRAND.orange,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back to login
                  </Typography>
                </Box>
              </Box>
            </form>
          )}
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Login screen ────────────────────────────────────────────────

  if (!authenticated) {
    return (
      <Box sx={{ ...fullPageSx }}>
        <Box sx={{ ...cardSx }}>
          <PortalHeader />

          {loginError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '10px',
                border: '1px solid rgba(239,68,68,0.3)',
                fontSize: '13px',
                py: 0.5,
              }}
            >
              {loginError}
            </Alert>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                placeholder="Email"
                type="email"
                variant="outlined"
                fullWidth
                required
                autoFocus
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loginLoading}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon
                        sx={{ color: BRAND.green, fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <TextField
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loginLoading}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{
                          color: '#94a3b8',
                          '&:hover': {
                            color: BRAND.orange,
                            bgcolor: 'rgba(239,125,0,0.08)',
                          },
                        }}
                      >
                        {showPassword ? (
                          <HideIcon fontSize="small" />
                        ) : (
                          <ShowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...inputSx,
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 0',
                    fontSize: '14px',
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    size="small"
                    sx={{
                      color: TEXT.secondary,
                      '&.Mui-checked': { color: BRAND.green },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize="13px"
                  >
                    Remember me on this device
                  </Typography>
                }
                sx={{ ml: 0, mt: -0.5, mb: 0 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!email.trim() || !password || loginLoading}
                startIcon={
                  loginLoading ? undefined : (
                    <LoginIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={submitBtnSx}
              >
                {loginLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  'Login'
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                <Typography
                  component="span"
                  onClick={() => setView('forgot-password')}
                  sx={{
                    color: BRAND.grayLight,
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: BRAND.orange,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>
            </Box>
          </form>
          <AppFooter />
        </Box>
      </Box>
    );
  }

  // ── Dashboard ───────────────────────────────────────────────────

  return (
    <React.Suspense
      fallback={
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafb',
          }}
        >
          <CircularProgress sx={{ color: BRAND.green }} />
        </Box>
      }
    >
      <DeveloperPortal jwtToken={jwtToken} onLogout={handleLogout} />
    </React.Suspense>
  );
};

export default App;
